import { useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useHolidayNotices } from "@/hooks/useHolidayNotices";
import { useBlockHoliday } from "@/hooks/useBlockHoliday";
import { isoToZoneParts } from "@/components/psychologist/calendar/timeZoneHelpers";
import { formatHolidayDay } from "@/utils/holidays";
import { calendarPath } from "@/routes/slugs";
import { activityTracker } from "@/utils/activityTracker";

/**
 * Aviso de feriados arriba de la Agenda (la primera pantalla al entrar, en la web y en la app):
 * mismo estilo que el aviso de Comunidad. Un solo aviso con una línea por feriado de los
 * próximos 14 días, con su estado (ver `useHolidayNotices`). "Bloquear mi agenda" usa el mismo
 * camino que Comunidad y que la marca del feriado en cada día (`useBlockHoliday`). "Voy a
 * trabajar" lo saca del aviso; se puede cambiar de idea desde la marca del feriado en la Agenda.
 */
/** `className`: margen exterior según dónde se monta (no deja espacio vacío si no hay aviso). */
export const HolidayAgendaNotice = ({
  className = "",
}: {
  className?: string;
}) => {
  const navigate = useNavigate();
  const { notices, timezone, countryName, marcarTrabajo } = useHolidayNotices();
  const { bloquear, checking, dialog } = useBlockHoliday(timezone);

  // "Voy a trabajar" ya decidió: ese feriado no se muestra más en el aviso.
  const visibles = notices.filter((n) => !n.trabaja);

  if (visibles.length === 0) return dialog;

  return (
    <div className={className}>
      <div
        data-testid="holiday-notice"
        className="flex flex-col gap-3 rounded-[20px] bg-white px-4 py-4 shadow-[0_6px_20px_rgba(109,87,188,0.08)] md:px-6"
      >
        {visibles.map((n) => (
          <div
            key={n.date}
            className="flex flex-wrap items-center gap-3"
            data-feriado={n.date}
          >
            <AlertTriangle size={24} className="shrink-0 text-amber-500" />
            <div className="flex min-w-0 flex-grow flex-col gap-0.5 md:min-w-[240px]">
              {n.state === 1 ? (
                <>
                  <span className="text-[13.5px] font-bold text-amber-700">
                    Se viene {n.name} en {countryName}, el{" "}
                    {formatHolidayDay(n.date)}
                  </span>
                  <span className="text-xs leading-snug text-gray-500">
                    ¿Vas a trabajar? Si no, bloqueá tu agenda para que no te
                    agenden sesiones ese día.
                  </span>
                </>
              ) : (
                <>
                  <span className="text-[13.5px] font-bold text-amber-700">
                    El {formatHolidayDay(n.date)} es {n.name} en {countryName} y
                    tenés {n.sessions.length}{" "}
                    {n.sessions.length === 1
                      ? "paciente agendado"
                      : "pacientes agendados"}
                  </span>
                  <span className="text-xs leading-snug text-[#4A3D73]">
                    {n.sessions
                      .map(
                        (s) =>
                          `${s.patientName || "Paciente"} ${isoToZoneParts(s.startIso, timezone).time} hs`,
                      )
                      .join(" · ")}
                  </span>
                  <span className="text-xs leading-snug text-gray-500">
                    Si no vas a trabajar ese día, avisales a tus pacientes y
                    reprogramá antes de bloquear.
                  </span>
                </>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {n.state === 1 ? (
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-600/90"
                  onClick={() => bloquear(n.date, n.name)}
                  disabled={checking !== null}
                >
                  {checking === n.date
                    ? "Revisando tu agenda…"
                    : "Bloquear mi agenda"}
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-600/90"
                  onClick={() =>
                    navigate(`${calendarPath("day")}?date=${n.date}`)
                  }
                >
                  Ver ese día en la Agenda
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700"
                onClick={() => {
                  // Si había abierto el panel de bloqueo y decidió trabajar, se cierra solo.
                  window.dispatchEvent(new CustomEvent("psi-block-close"));
                  activityTracker.trackAction("feriado_voy_a_trabajar");
                  marcarTrabajo(n.date);
                }}
              >
                Voy a trabajar
              </Button>
            </div>
          </div>
        ))}
      </div>
      {dialog}
    </div>
  );
};

export default HolidayAgendaNotice;
